/**
 * Cliente HTTP da API.
 *
 * Caminhos sempre relativos (`/api/...`): em producao o Fastify serve o front e
 * a API na mesma origem, e em desenvolvimento o Vite encaminha /api. Nao existe
 * URL de API em variavel de ambiente do front - uma a menos para errar no
 * deploy.
 */

export interface ApiErrorBody {
  error: {
    code: string
    message: string
    fields?: Record<string, string[]>
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** Erros por campo, prontos para o formulario exibir ao lado do input. */
    readonly fields?: Record<string, string[]>,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** 401: sessao ausente ou expirada - o app manda para o login. */
  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  query?: Record<string, string | number | boolean | undefined | null>
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  if (!query) return path
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    params.set(key, String(value))
  }
  const queryString = params.toString()
  return queryString ? `${path}?${queryString}` : path
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, query } = options

  const response = await fetch(buildUrl(path, query), {
    method,
    // Em desenvolvimento o front esta em outra porta; sem isso o cookie de
    // sessao nao acompanharia a requisicao.
    credentials: 'include',
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  })

  if (response.status === 204) return undefined as T

  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.includes('application/json')) {
    if (response.ok) return undefined as T
    // HTML numa resposta de erro normalmente significa proxy ou servidor fora
    // do ar; a mensagem crua nao ajudaria quem esta usando o painel.
    throw new ApiError(
      response.status,
      'RESPOSTA_INVALIDA',
      'Nao foi possivel falar com o servidor. Verifique sua conexao e tente de novo.',
    )
  }

  const payload = (await response.json()) as unknown

  if (!response.ok) {
    const parsed = payload as Partial<ApiErrorBody>
    throw new ApiError(
      response.status,
      parsed.error?.code ?? 'ERRO',
      parsed.error?.message ?? 'Ocorreu um erro inesperado.',
      parsed.error?.fields,
    )
  }

  return payload as T
}

export const apiGet = <T>(path: string, query?: RequestOptions['query']) =>
  api<T>(path, { method: 'GET', query })

export const apiPost = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'POST', body })

/**
 * Substituicao completa de um recurso.
 *
 * Faltava aqui, e a ausencia causou um erro real: o construtor de formulario de
 * evento chamava `apiPost` numa rota que a API registra como PUT, e salvar
 * respondia "endpoint nao encontrado". `npm run check:rotas` compara os dois
 * lados para que isso nao volte em silencio.
 */
export const apiPut = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'PUT', body })

export const apiPatch = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'PATCH', body })

export const apiDelete = <T>(path: string) => api<T>(path, { method: 'DELETE' })

/**
 * Envia o arquivo direto ao bucket pela URL assinada.
 *
 * Nao usa o cliente acima de proposito: a requisicao vai para o S3/R2, nao para
 * a API, e enviar cookie de sessao para outro dominio seria vazamento.
 */
export async function uploadToSignedUrl(
  uploadUrl: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open('PUT', uploadUrl, true)
    request.setRequestHeader('content-type', file.type)

    // XHR e nao fetch porque so ele reporta progresso de upload - com fotos de
    // celular em 4G, a barra de progresso e a diferenca entre esperar e
    // achar que travou.
    if (onProgress) {
      request.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100))
        }
      })
    }

    request.addEventListener('load', () => {
      if (request.status >= 200 && request.status < 300) resolve()
      else reject(new Error(`Falha no envio do arquivo (${request.status}).`))
    })
    request.addEventListener('error', () =>
      reject(new Error('Falha de rede ao enviar o arquivo.')),
    )
    request.addEventListener('abort', () => reject(new Error('Envio cancelado.')))

    request.send(file)
  })
}
